import {Step, Store, combine} from '@effector/effector'
import {
  nodes$,
  Node,
  Edge,
  NodeWatch,
  NodeEffect,
  NodeEvent,
  NodeStore,
  NodeSample,
  ID,
  NodeOn,
  NodeForward
} from '../workingSet'

export type RuntimeStore = {
  name: string
  initialValue: any
  key: symbol
  unit: 'store'
  node: Step
  type: string
  sid?: string
}
export type RuntimeEvent = {
  name: string
  key: symbol
  unit: 'event'
  node: Step
}
export type RuntimeEffect = {
  name: string
  key: symbol
  unit: 'effect'
  handler: Function
  node: Step
}
export type RuntimeWatch = {
  key: symbol
  unit: 'watch'
  sources: string
  owner: string
  node: Step
}
export type RuntimeOn = {
  key: symbol
  unit: 'on'
  sources: string
  source: string
  clock: string
  node: Step
  isReset?: boolean
}
export type RuntimeSample = {
  key: symbol
  unit: 'sample'
  source: string
  clock: string
  target: string
  node: Step
}
export type RuntimeForward = {
  key: symbol
  unit: 'forward'
  source: string
  target: string
  node: Step
}
export type RuntimePrimeUnit = RuntimeStore | RuntimeEvent | RuntimeEffect
export type RuntimeUnit =
  | RuntimeStore
  | RuntimeEvent
  | RuntimeEffect
  | RuntimeWatch
  | RuntimeOn
  | RuntimeSample
  | RuntimeForward
export const unitIs = {
  store: (unit: RuntimeUnit): unit is RuntimeStore => unit.unit === 'store',
  event: (unit: RuntimeUnit): unit is RuntimeEvent => unit.unit === 'event',
  effect: (unit: RuntimeUnit): unit is RuntimeEffect => unit.unit === 'effect',
  watch: (unit: RuntimeUnit): unit is RuntimeWatch => unit.unit === 'watch',
  on: (unit: RuntimeUnit): unit is RuntimeOn => unit.unit === 'on',
  sample: (unit: RuntimeUnit): unit is RuntimeOn => unit.unit === 'sample',
  forward: (unit: RuntimeUnit): unit is RuntimeForward =>
    unit.unit === 'forward'
}
type SelectUnits<T> = {
  store: (unit: RuntimeStore) => T
  event: (unit: RuntimeEvent) => T
  effect: (unit: RuntimeEffect) => T
  watch: (unit: RuntimeWatch) => T
  on: (unit: RuntimeOn) => T
  sample: (unit: RuntimeSample) => T
  forward: (unit: RuntimeForward) => T
}
export function selectUnits<T>(shape: Partial<SelectUnits<T>>): Store<T[]> {
  const selected = [] as Array<Store<T[]>>
  const map = {
    store: stores,
    event: events,
    effect: effects,
    watch: watches,
    on: ons,
    sample: samples,
    forward: forwards
  }
  for (const key in shape) {
    selected.push(map[key].map(list => list.map(shape[key])))
  }
  return combine(selected, lists => {
    const result = [] as T[]
    for (const list of lists) {
      result.push(...list)
    }
    return result
  })
}
export const stores = nodes$.map(nodes => {
  const result = [] as RuntimeStore[]
  for (const node_ of nodes) {
    const node: NodeStore = node_ as any
    if (node.unit === 'store' && !node.exclude) {
      //@ts-ignore
      const id = node.node.seq[3].data.target.id
      //@ts-ignore
      const value = node.node.reg[id].current
      let name = node.label
      //@ts-ignore
      if (node.node.meta.isComposite) {
        //@ts-ignore
        name = node.node.meta.compositeName
      }
      result.push({
        name,
        initialValue: value,
        //@ts-ignore
        value,
        nullable: value === null,
        type: typeof value,
        key: Symbol('store key'),
        unit: 'store',
        node: node.node
      })
    }
  }
  return result
})
export const events = nodes$.map(nodes => {
  const result = [] as RuntimeEvent[]
  for (const node_ of nodes) {
    const node: NodeEvent = node_ as any
    if (node.unit === 'event') {
      result.push({
        name: node.label,
        key: Symbol('event key'),
        unit: 'event',
        node: node.node
      })
    }
  }
  return result.reverse()
})
export const independentEvents = events.map(list =>
  list.filter(item => {
    if (item.name.includes('.')) return false
    switch (item.node.meta.named) {
      case 'done':
      case 'doneData':
      case 'fail':
      case 'failData':
      case 'finally':
        return false
    }
    switch (item.name) {
      case 'updates':
      case 'onDomain':
      case 'onStore':
      case 'onEvent':
      case 'onEffect':
        return false
    }
    return true
  })
)
export const effects = nodes$.map(nodes => {
  const result = [] as RuntimeEffect[]
  for (const node_ of nodes) {
    const node: NodeEffect = node_ as any
    if (node.unit === 'effect' && !node.exclude) {
      result.push({
        name: node.label,
        key: Symbol('effect key'),
        unit: 'effect',
        handler: node.node.scope.runner.scope.getHandler.original(),
        node: node.node
      })
    }
  }
  return result.reverse()
})
export const watches = nodes$.map(nodes => {
  const result = [] as RuntimeWatch[]
  for (const node_ of nodes) {
    const node: NodeWatch = node_ as any
    if (node.op === 'watch' && !node.exclude) {
      result.push({
        key: Symbol('watch key'),
        unit: 'watch',
        sources: node.sources,
        owner: getLabelByID(node.owner, nodes),
        node: node.node
      })
    }
  }
  return result.reverse()
})
export const ons = nodes$.map(nodes => {
  const result = [] as RuntimeOn[]
  for (const node_ of nodes) {
    const node: NodeOn = node_ as any
    if (node.op === 'on' && !node.exclude) {
      result.push({
        key: Symbol('on key'),
        unit: 'on',
        sources: node.sources,
        source: getLabelByID(node.source, nodes),
        clock: getLabelByID(node.clock, nodes),
        node: node.node
      })
    }
  }
  return result.reverse()
})
export const samples = nodes$.map(nodes => {
  const result = [] as RuntimeSample[]
  for (const node_ of nodes) {
    const node: NodeSample = node_ as any
    if (node.op === 'sample' && !node.exclude) {
      result.push({
        key: Symbol('sample key'),
        unit: 'sample',
        source: getLabelByID(node.source, nodes),
        clock: getLabelByID(node.clock, nodes),
        target: getLabelByID(node.target, nodes),
        node: node.node
      })
    }
  }
  return result
})
export const forwards = nodes$.map(nodes => {
  const result = [] as RuntimeForward[]
  for (const node_ of nodes) {
    const node: NodeForward = node_ as any
    if (node.op === 'forward' && !node.exclude) {
      result.push({
        key: Symbol('forward key'),
        unit: 'forward',
        source: getLabelByID(node.source, nodes),
        target: getLabelByID(node.target, nodes),
        node: node.node
      })
    }
  }
  return result.reverse()
})
export const primeUnits = combine(
  stores,
  events,
  effects,
  (stores, events, effects) =>
    [...stores, ...events, ...effects] as RuntimePrimeUnit[]
)
function getLabelByID(id: ID, nodes: Node[]) {
  const item = nodes.find(node => node.id === id)
  if (!item) throw Error('not found')
  return item.label
}
